import { currentUrlWithExtraQueryParams } from '../../../utils/utils';
import { postStartPayment } from '../../../api';
import { CodeStatus, PaymentCustomer, PaymentOption } from './types';
import { ErrorType } from '../../rtr-errors/utils';
import { t } from '../../../redux/utils';

interface PayParams {
  isOrder: boolean;
  selectedMethod: string;
  stripe: any;
  orderReference: string;
  amountToPay: number;
  customer: PaymentCustomer;
  stripeCcRef: any;
  stripeIbanRef: any;
  sepaName: any;
  blikCode: string;
  payPalPlusPaymentId: any;
  payPrepayment: any;
  payWithStripeCC: any;
  payWithStripeSofort: any;
  payWithStripeIdeal: any;
  payWithStripeSepa: any;
  payWithStripeBancontact: any;
  payWithStripeP24: any;
  payWithStripeBlik: any;
  payWithStripeTwint: any;
  payWithPayPal: any;
  payPalPlusUpdate: any;
  reserve: any;
  payWithVoucher: any;
  confirmPayWithStripeCC: any;
  completeWithStripeSepa: any;
  prepay: CodeStatus;
  onSuccess: (params?: SuccessParams) => void;
  onError: (errMsg?: string) => void;
  onBlikSuccess: () => void;
}

interface PrepayProps {
  payPrepayment: any;
  orderReference: string;
  prepay: CodeStatus;
  onSuccess: (params?: SuccessParams) => void;
  onError?: (errMsg?: string) => void;
}

interface BancontactProps {
  isOrder: boolean;
  payBancontact: any;
  stripe: any;
  orderReference: string;
  customer: PaymentCustomer;
  onSuccess: (params?: SuccessParams) => void;
  onError?: (errMsg?: string) => void;
}

interface P24Props {
  isOrder: boolean;
  payWithP24: any;
  stripe: any;
  orderReference: string;
  customer: PaymentCustomer;
  onSuccess: (params?: SuccessParams) => void;
  onError?: (errMsg?: string) => void;
}

interface BlikProps {
  isOrder: boolean;
  payWithBlik: any;
  stripe: any;
  orderReference: string;
  code: string;
  customer: any;
  onSuccess: (params?: SuccessParams) => void;
  onError?: (errMsg?: string) => void;
}

interface TwintProps {
  isOrder: boolean;
  payWithTwint: any;
  stripe: any;
  orderReference: string;
  customer: PaymentCustomer;
  onSuccess: (params?: SuccessParams) => void;
  onError?: (errMsg?: string) => void;
}

interface SofortProps {
  isOrder: boolean;
  paySofort: any;
  stripe: any;
  orderReference: string;
  customer: PaymentCustomer;
  onSuccess: (params?: SuccessParams) => void;
  onError?: (errMsg?: string) => void;
}

interface SepaProps {
  isOrder: boolean;
  paySepa: any;
  completeSepa: any;
  stripeIbanRef: any;
  sepaName: any;
  stripe: any;
  orderReference: string;
  customer: PaymentCustomer;
  onSuccess: (params?: SuccessParams) => void;
  onError?: (errMsg?: string) => void;
}

interface StripeIdealProps {
  isOrder: boolean;
  payStripeIdeal: any;
  stripe: any;
  orderReference: string;
  customer: PaymentCustomer;
  onSuccess: (params?: SuccessParams) => void;
  onError?: (errMsg?: string) => void;
}

interface StripeCCProps {
  isOrder: boolean;
  payStripeCC: any;
  stripeCcRef: any;
  stripe: any;
  orderReference: string;
  confirmStripeCC: any;
  onSuccess: (params?: SuccessParams) => void;
  onError?: (errMsg?: string) => void;
}

export interface SuccessParams {
  url?: string;
  goPayPal?: boolean;
  goNextStep?: boolean;
}

export const pay = async ({
  isOrder,
  selectedMethod,
  stripe,
  orderReference,
  amountToPay,
  customer,
  stripeCcRef,
  stripeIbanRef,
  sepaName,
  blikCode,
  payPalPlusPaymentId,
  payPrepayment,
  payWithStripeCC,
  payWithStripeSofort,
  payWithStripeIdeal,
  payWithStripeSepa,
  payWithStripeBancontact,
  payWithStripeP24,
  payWithStripeBlik,
  payWithStripeTwint,
  payWithPayPal,
  payPalPlusUpdate,
  confirmPayWithStripeCC,
  reserve,
  payWithVoucher,
  completeWithStripeSepa,
  prepay,
  onSuccess,
  onError,
  onBlikSuccess,
}: PayParams) => {
  // remove unload prompt (as it is expected that for some psp user will be redirected to external page)
  window.onbeforeunload = undefined;

  const canPay = amountToPay > 0 || (isOrder && selectedMethod === PaymentOption.Reservation);

  if (canPay) {
    postStartPayment(
      isOrder,
      orderReference,
      () => {
        switch (selectedMethod) {
          case PaymentOption.Prepayment:
            if (isOrder) {
              payWithPrepayment({ payPrepayment, orderReference, prepay, onSuccess, onError });
            }
            break;
          case PaymentOption.Reservation:
            if (isOrder) {
              reserve(orderReference, onSuccess, onError);
            }
            break;
          case PaymentOption.StripeCC:
            payStripe({
              isOrder: isOrder,
              payStripeCC: payWithStripeCC,
              stripeCcRef: stripeCcRef,
              confirmStripeCC: confirmPayWithStripeCC,
              stripe: stripe,
              orderReference: orderReference,
              onSuccess: onSuccess,
              onError: onError,
            });
            break;
          case PaymentOption.PayPal:
            payWithPayPal(orderReference, '',isOrder, data => onSuccess({ url: data.approvalUrl }), onError);
            break;
          case PaymentOption.PayPalPlus:
            payPalPlusUpdate(orderReference, payPalPlusPaymentId, isOrder, () => onSuccess({ goPayPal: true }), onError);
            break;
          case PaymentOption.StripeSofort:
            paySofort({
              isOrder,
              paySofort: payWithStripeSofort,
              stripe: stripe,
              orderReference: orderReference,
              customer: customer,
              onSuccess: onSuccess,
              onError: onError,
            });
            break;
          case PaymentOption.StripeIdeal:
            payStripeIdeal({
              isOrder,
              payStripeIdeal: payWithStripeIdeal,
              stripe: stripe,
              orderReference: orderReference,
              customer: customer,
              onSuccess: onSuccess,
              onError: onError,
            });
            break;
          case PaymentOption.StripeSepa:
            paySepa({
              isOrder,
              paySepa: payWithStripeSepa,
              completeSepa: completeWithStripeSepa,
              stripeIbanRef: stripeIbanRef,
              sepaName: sepaName,
              stripe: stripe,
              orderReference: orderReference,
              customer: customer,
              onSuccess: onSuccess,
              onError: onError,
            });
            break;
          case PaymentOption.StripeBanContact:
            payBancontact({
              isOrder,
              payBancontact: payWithStripeBancontact,
              stripe: stripe,
              orderReference: orderReference,
              customer: customer,
              onSuccess: onSuccess,
              onError: onError,
            });
            break;
          case PaymentOption.StripeP24:
            payP24({
              isOrder,
              payWithP24: payWithStripeP24,
              stripe: stripe,
              orderReference: orderReference,
              customer: customer,
              onSuccess: onSuccess,
              onError: onError,
            });
            break;
          case PaymentOption.StripeBlik:
            payBlik({
              isOrder,
              payWithBlik: payWithStripeBlik,
              stripe: stripe,
              orderReference: orderReference,
              code: blikCode,
              customer: customer,
              onSuccess: params => {
                onSuccess(params);
                onBlikSuccess();
              },
              onError: onError,
            });
            break;
          case PaymentOption.StripeTwint:
            payTwint({
              isOrder,
              payWithTwint: payWithStripeTwint,
              stripe: stripe,
              orderReference: orderReference,
              customer: customer,
              onSuccess: onSuccess,
              onError: onError,
            });
            break;
        }
      },
      onError,
    );
  } else {
    if (isOrder) {
      payWithVoucher(orderReference, onSuccess, onError);
    }
  }
};

const payWithPrepayment = async ({ payPrepayment, orderReference, prepay, onSuccess, onError }: PrepayProps) => {
  if (prepay !== CodeStatus.NotNeeded && prepay !== CodeStatus.Accepted) {
    onError(t(`error.${ErrorType.PaymentProblemWithPasscode}`));
    return false;
  }

  payPrepayment(orderReference, onSuccess, onError);
};

const payBancontact = async ({ isOrder, payBancontact, stripe, orderReference, customer, onSuccess, onError }: BancontactProps) => {
  payBancontact(
    orderReference,
    isOrder,
    intentClientSecret => {
      stripe
        .confirmBancontactPayment(intentClientSecret, {
          payment_method: {
            billing_details: {
              name: customer.fullName,
            },
          },
          return_url: currentUrlWithExtraQueryParams([
            { name: 'rentware-payment', value: 'stripe-source' },
            { name: 'rentware-order-id', value: orderReference },
          ]),
        })
        .then(() => {
          onSuccess();
        })
        .catch(onError);
    },
    onError,
  );
};

const paySofort = async ({ isOrder, paySofort, stripe, orderReference, customer, onSuccess, onError }: SofortProps) => {
  paySofort(
    orderReference,
    isOrder,
    intentClientSecret => {
      stripe
        .confirmSofortPayment(intentClientSecret, {
          payment_method: {
            sofort: {
              country: customer.country,
            },
          },
          return_url: currentUrlWithExtraQueryParams([
            { name: 'rentware-payment', value: 'stripe-source' },
            { name: 'rentware-order-id', value: orderReference },
          ]),
        })
        .then(() => {
          onSuccess();
        })
        .catch(() => {
          onError();
        });
    },
    onError,
  );
};

const paySepa = async ({ isOrder, paySepa, completeSepa, stripeIbanRef, sepaName, stripe, orderReference, customer, onSuccess, onError }: SepaProps) => {
    paySepa(
        orderReference,
        isOrder,
        intentClientSecret => {
          stripe
            .confirmSepaDebitPayment(intentClientSecret, {
              payment_method: {
                sepa_debit: stripeIbanRef,
                billing_details: {
                  name: sepaName,
                  email: customer.email,
                },
              },
              return_url: currentUrlWithExtraQueryParams([
                { name: 'rentware-payment', value: 'stripe-source' },
                { name: 'rentware-order-id', value: orderReference },
              ]),
            })
            .then((data) => {
                const { error, paymentIntent } = data;
                if (error || (paymentIntent.status !== 'succeeded' && paymentIntent.status !== 'processing')) {
                    onError();

                    return;
                }

                onSuccess();
                completeSepa();
            })
            .catch(onError);
        },
        onError,
    );
};

const payStripeIdeal = async ({ isOrder, payStripeIdeal, stripe, orderReference, customer, onSuccess, onError }: StripeIdealProps) => {
  payStripeIdeal(
    orderReference,
    isOrder,
    intentClientSecret => {
      stripe
        .confirmIdealPayment(intentClientSecret, {
          payment_method: {
            ideal: {},
            billing_details: {
              name: customer.fullName,
            },
          },
          return_url: currentUrlWithExtraQueryParams([
            { name: 'rentware-payment', value: 'stripe-source' },
            { name: 'rentware-order-id', value: orderReference },
          ]),
        })
        .then(() => {
          onSuccess();
        })
        .catch(onError);
    },
    onError,
  );
};

const payP24 = async ({ isOrder, payWithP24, stripe, orderReference, customer, onSuccess, onError }: P24Props) => {
  payWithP24(
    orderReference,
    isOrder,
    intentClientSecret => {
      stripe
        .confirmP24Payment(intentClientSecret, {
          payment_method: {
            billing_details: {
              email: customer.email,
            },
          },
          return_url: currentUrlWithExtraQueryParams([
            { name: 'rentware-payment', value: 'stripe-source' },
            { name: 'rentware-order-id', value: orderReference },
          ]),
        })
        .then(() => {
          onSuccess();
        })
        .catch(onError);
    },
    onError,
  );
};

export const payBlik = async ({ isOrder, payWithBlik, stripe, orderReference, code, customer, onSuccess, onError }: BlikProps) => {
  payWithBlik(
    orderReference,
    isOrder,
    intentClientSecret => {
      stripe
        .confirmBlikPayment(intentClientSecret, {
          payment_method: {
            blik: {},
            billing_details: {
              email: customer.email,
            },
          },
          payment_method_options: {
            blik: {
              code: code,
            },
          },
        })
        .then(data => {
          // from docs: confirmBlikPayment doesn’t return until the confirmation succeeds or fails
          const { error, paymentIntent } = data;
          if (error || paymentIntent.status !== 'succeeded') {
            onError(error);

            return;
          }

          onSuccess();
        })
        .catch(onError);
    },
    onError,
  );
};

const payTwint = async ({ isOrder, payWithTwint, stripe, orderReference, customer, onSuccess, onError }: TwintProps) => {
  payWithTwint(
    orderReference,
    isOrder,
    intentClientSecret => {
      stripe
        .confirmTwintPayment(intentClientSecret, {
          payment_method: {
            billing_details: {
              name: customer.fullName,
              email: customer.email,
            },
          },
          return_url: currentUrlWithExtraQueryParams([
            { name: 'rentware-payment', value: 'stripe-source' },
            { name: 'rentware-order-id', value: orderReference },
          ]),
        })
        .then(() => {
          onSuccess();
        })
        .catch(onError);
    },
    onError,
  );
};


const payStripe = async ({ isOrder, payStripeCC, stripeCcRef, confirmStripeCC, stripe, orderReference, onSuccess, onError }: StripeCCProps) => {
  const resp = await stripe.createPaymentMethod({
    type: 'card',
    card: stripeCcRef,
  });

  if (resp.error) {
    const message = resp.error?.message || t(`error.${ErrorType.PaymentNotSucceed}`);

    onError(message);
    return false;
  }

  payStripeCC(
    orderReference,
    isOrder,
    resp.paymentMethod,
    (data: any) => {
      if (data.requiresAction) {
        stripe.handleCardAction(data.paymentIntentClientSecret).then(result => {
          if (result.error) {
            onError(result.error.message);
          } else {
            confirmStripeCC(result.paymentIntent.id, isOrder, onSuccess, onError);
          }
        });
      } else {
        onSuccess({ goNextStep: true });
      }
    },
    () => {
      onError(t(`error.${ErrorType.PaymentNotSucceed}`));
    },
  );
};
